/**
 * Shared `useTrackedDays` — the single canonical React Query hook for the
 * user's entire history of marked attendance days.
 *
 * Historical context:
 *   Dashboard, BreakfastModal, and Analytics each carried their own copy of
 *   this query, each with a subtly different select list:
 *     • Dashboard:      `date, system_day_type_id, status`
 *     • Analytics:      `date, system_day_type_id, status`
 *     • BreakfastModal: `date, system_day_type_id` (no status)
 *   Every mutation in the codebase invalidates `["trackedDays"]` — so the
 *   modal's "lite" copy was getting refetched on every write anyway, but
 *   returning a narrower shape than the rest of the app expected. That
 *   asymmetry produced at least one production bug where an optimistic
 *   status update disappeared after a refetch.
 *
 * Canonical shape matches `MarkedDay` from `@/contexts/AppContext`. Consumers
 * that don't need `status` can simply ignore it.
 */

import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { MarkedDay } from "@/contexts/AppContext";

// ─── Query keys (centralized — NEVER use the string literal directly) ──────
export const TRACKED_DAYS_ROOT = "trackedDays" as const;

/** Prefix key — pass to `invalidateQueries` / `removeQueries` for fan-out. */
export const trackedDaysPrefix = [TRACKED_DAYS_ROOT] as const;

/** Full query key for a specific user. */
export function trackedDaysKey(userId: string | null | undefined) {
  return [TRACKED_DAYS_ROOT, userId ?? null] as const;
}

// ─── Supabase row shape ────────────────────────────────────────────────────
interface TrackedDayRow {
  date: string;
  system_day_type_id: string;
  status: string | null;
}

/**
 * Hard ceiling on the number of tracked-day rows fetched in a single query.
 *
 * Audit W3: `fetchTrackedDays` had no top-level bound, so a long-tenure
 * user with years of daily history would pull the entire table into memory
 * via React Query. Three years of daily confirmations is ~1,100 rows, so
 * 5,000 comfortably covers "many years" while still capping worst-case
 * payload size. If we ever exceed it, consumers should move to a dated
 * window (see `useBreakfastEvents` for the pattern).
 */
const TRACKED_DAYS_HARD_LIMIT = 5000;

async function fetchTrackedDays(userId: string): Promise<MarkedDay[]> {
  const { data, error } = await supabase
    .from("tracked_days")
    .select("date, system_day_type_id, status")
    .eq("user_id", userId)
    .order("date", { ascending: false })
    .limit(TRACKED_DAYS_HARD_LIMIT);
  if (error) throw error;
  return ((data ?? []) as TrackedDayRow[]).map((row) => ({
    date:   row.date,
    typeId: row.system_day_type_id,
    status: (row.status as "confirmed" | "unconfirmed" | null) ?? undefined,
  }));
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export type UseTrackedDaysOptions = Pick<
  UseQueryOptions<MarkedDay[]>,
  "enabled" | "staleTime" | "gcTime" | "refetchOnWindowFocus" | "throwOnError"
>;

/**
 * Resolves the user's full `tracked_days` history as `MarkedDay[]`.
 *
 * Defaults: 5-minute staleTime. This query is invalidated explicitly by every
 * attendance / RSVP mutation in the codebase (see `useAttendanceMutations`,
 * `useBreakfastMutations.*`, etc.), so aggressive caching is safe here.
 */
export function useTrackedDays(
  userId: string | null | undefined,
  options: UseTrackedDaysOptions = {},
): UseQueryResult<MarkedDay[]> {
  return useQuery<MarkedDay[]>({
    queryKey: trackedDaysKey(userId),
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: options.staleTime ?? 5 * 60_000,
    gcTime: options.gcTime ?? 10 * 60_000,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    throwOnError: options.throwOnError ?? false,
    queryFn: () => fetchTrackedDays(userId!),
  });
}
